import { useState } from "react";

/** 任务名上限:与后端 MediaTaskService.TASK_NAME_MAX_LEN 同值(后端超长会截断,前端先拦住)。 */
export const TASK_NAME_MAX_LEN = 60;

export interface InlineRename {
  /** 是否处于编辑态(标题渲染成输入框)。 */
  editing: boolean;
  /** 进编辑态(操作行的「重命名」按钮调)。 */
  begin: () => void;
  /** 直接摊给 <input> 的 props(value/onChange/onBlur/onKeyDown/maxLength/autoFocus)。 */
  inputProps: {
    autoFocus: boolean;
    value: string;
    maxLength: number;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  };
}

/**
 * 卡片内联重命名的状态机(海报/视频/分身三线共用)。
 *
 * 2026-07-15 三线列表对齐:海报卡早有内联重命名(pencil 进编辑态 → Enter/失焦存、Esc 取消),
 * 视频/分身完全没有。抽成 hook 而不是把标题也塞进共享组件 —— 三线的标题渲染各不相同
 * (海报要按 " - " 拆模板名/副名,视频要 line-clamp,分身要「口播视频 #id」兜底),
 * 共享**状态机**、各自渲染标题,才是真正能三线共用的切法。
 *
 * 与操作行的关系:`begin` 给 <MediaCardActionRow onRename>,`editing`/`inputProps` 给标题区 —— 两者
 * 由调用方在同一张卡里组合(见 PosterDashboardPage#HistoryCard)。
 *
 * 语义(照海报既有实现,用户已习惯):
 * - 空白 / 纯空格不提交(不许把名字清空;后端 updateMeta 也有同款守卫);
 * - 与原名相同不提交(省掉一次白跑的 PATCH + 列表 invalidate);
 * - Esc 取消不落库;Enter / 失焦提交。
 */
export function useInlineRename({
  name,
  onRename,
  maxLength = TASK_NAME_MAX_LEN,
}: {
  name: string | null | undefined;
  onRename: (next: string) => void;
  maxLength?: number;
}): InlineRename {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const begin = () => {
    setDraft(name || "");
    setEditing(true);
  };

  const commit = () => {
    const next = draft.trim();
    if (next && next !== name) onRename(next);
    setEditing(false);
  };

  return {
    editing,
    begin,
    inputProps: {
      autoFocus: true,
      value: draft,
      maxLength,
      onChange: (e) => setDraft(e.target.value),
      onBlur: commit,
      onKeyDown: (e) => {
        if (e.key === "Enter") commit();
        // Esc 取消:先关编辑态,不提交。stopPropagation 防冒泡到外层(如 lightbox/Dialog 的 Esc 关闭)。
        else if (e.key === "Escape") {
          e.stopPropagation();
          setEditing(false);
        }
      },
    },
  };
}
